import { adminApiClient } from './adminApiClient';
import { createNestedCrudService } from './createCrudService';
import type { WorkflowStep } from './workflowDefinitionsService';

// Re-export for convenience
export type { WorkflowStep } from './workflowDefinitionsService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkflowRuleTenant {
  id: string;
  name: string;
  slug: string;
}

export interface WorkflowRuleTargetStep {
  id: string;
  name: string;
  sort_order: number;
}

export interface ReplacementStepData {
  step_type: string;
  name: string;
  config?: Record<string, unknown> | null;
  execution_mode?: 'sync' | 'async';
  condition?: Record<string, unknown> | null;
  timeout_seconds?: number | null;
  retry_policy?: Record<string, unknown> | null;
}

export interface WorkflowRule {
  id: string;
  workflow_definition_id: string;
  tenant_id: string;
  tenant: WorkflowRuleTenant;
  rule_type: 'activate' | 'inactivate' | 'replace';
  target_step_id: string;
  target_step: WorkflowRuleTargetStep;
  replacement_step_data: ReplacementStepData | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StoreWorkflowRuleRequest {
  tenant_id: string;
  rule_type: 'activate' | 'inactivate' | 'replace';
  target_step_id: string;
  replacement_step_data?: ReplacementStepData | null;
  is_active?: boolean;
}

export interface UpdateWorkflowRuleRequest {
  tenant_id?: string;
  rule_type?: 'activate' | 'inactivate' | 'replace';
  target_step_id?: string;
  replacement_step_data?: ReplacementStepData | null;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const buildBasePath = (workflowSlug: string) =>
  `/admin/workflows/${workflowSlug}/rules`;

const crudService = createNestedCrudService<
  WorkflowRule,
  StoreWorkflowRuleRequest,
  UpdateWorkflowRuleRequest
>(buildBasePath);

export const workflowRulesService = {
  ...crudService,

  async listByTenant(
    workflowSlug: string,
    tenantId: string,
  ): Promise<{ data: WorkflowRule[] }> {
    const response = await adminApiClient.get<{ data: WorkflowRule[] }>(
      `${buildBasePath(workflowSlug)}?tenant_id=${tenantId}`,
    );
    return response.data;
  },

  async getTenants(
    workflowSlug: string,
  ): Promise<{ data: WorkflowRuleTenant[] }> {
    const response = await adminApiClient.get<{ data: WorkflowRuleTenant[] }>(
      `${buildBasePath(workflowSlug)}/tenants`,
    );
    return response.data;
  },

  async getEffectiveSteps(
    workflowSlug: string,
    tenantId: string,
  ): Promise<{ data: WorkflowStep[] }> {
    const response = await adminApiClient.get<{ data: WorkflowStep[] }>(
      `/admin/workflows/${workflowSlug}/effective-steps?tenant_id=${tenantId}`,
    );
    return response.data;
  },
};
